import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import Typography from '@mui/material/Typography'
import { useIsFetching } from '@tanstack/react-query'
import KNDataTable from 'components/KNDataTable/KNDataTable'
import type { KNDataTableAction, KNDataTableColumn } from 'components/KNDataTable/types'
import { Role } from 'context/authentication/Role.types'
import { useDriversView } from 'context/DriversViewContext'
import { analyticsEvent } from 'global/helpers/analytics'
import { hasAccess } from 'global/helpers/authorization'
import { replaceHighlights } from 'global/helpers/highlights'
import { getSharingLanguages } from 'global/helpers/languages'
import useDialog from 'global/hooks/useDialog'
import { type ReactElement, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CreateOrEditDriverDialog from './CreateOrEditDriverDialog'
import DeleteDriverDialog from './DeleteDriverDialog'
import { DRIVERS_QUERY_KEY } from './DriverManager.service'
import type { DriverData } from './DriverManager.types'

interface DriverTableProps {
  readonly drivers: DriverData[]
}

const DriverTable = ({ drivers }: DriverTableProps): ReactElement => {
  const { t } = useTranslation()
  const [activeDriver, setActiveDriver] = useState<DriverData>()
  const { filters } = useDriversView()
  const createOrEditDriverDialog = useDialog<typeof CreateOrEditDriverDialog>()
  const deleteDriverDialog = useDialog<typeof DeleteDriverDialog>()
  const isFetching = useIsFetching({ queryKey: [DRIVERS_QUERY_KEY] })
  const languages = getSharingLanguages()

  const handleEditClick = useCallback((row: DriverData) => {
    setActiveDriver(row)
    createOrEditDriverDialog.handleOpen()
    analyticsEvent('polestar_cs_edit_driver_button_clicked')
  }, [])

  const handleDeleteClick = useCallback((row: DriverData) => {
    setActiveDriver(row)
    deleteDriverDialog.handleOpen()
    analyticsEvent('polestar_cs_delete_driver_button_clicked')
  }, [])

  const nameColumnValue = (row: DriverData) => (
    <Typography variant="p3" color="text.main">{replaceHighlights(row.name, filters.keywords)}</Typography>
  )

  const languageColumnValue = (row: DriverData) => {
    const language = languages.find((language) => language.languageCode === row.language)
    if (language) {
      return (
        <Typography variant="p3" color="text.main">
          {language?.label}
        </Typography>
      )
    }
    return (
      <Typography variant="p3" color="primary.light">—</Typography>
    )
  }

  const phoneColumnValue = (row: DriverData) => (
    <Typography variant="p3" color="text.main">{replaceHighlights(row.contactNumber, filters.keywords)}</Typography>
  )

  const emailColumnValue = (row: DriverData) => (
    <Typography variant="p3" color="text.main">{replaceHighlights(row.email, filters.keywords)}</Typography>
  )

  const columns = useMemo((): KNDataTableColumn<DriverData>[] => [
    {
      name: 'name',
      label: t('screens.cs.driver_manager.card.columns.name'),
      getValue: nameColumnValue,
      sx: { width: { xs: '18rem', xxl: 'auto' } },
    },
    {
      name: 'language',
      label: t('screens.cs.driver_manager.card.columns.language'),
      getValue: languageColumnValue,
      sx: { width: '18rem' },
    },
    {
      name: 'contactNumber',
      label: t('screens.cs.driver_manager.card.columns.phone'),
      getValue: phoneColumnValue,
      sx: { width: '18rem' },
    },
    {
      name: 'email',
      label: t('screens.cs.driver_manager.card.columns.email'),
      getValue: emailColumnValue,
      sx: { width: { xs: '18rem', xxl: '24rem' } },
    },
  ], [filters.keywords])

  const actions = useMemo((): KNDataTableAction<DriverData>[] => [
    {
      name: 'edit',
      label: t('screens.cs.driver_manager.card.actions.edit_short'),
      icon: <EditIcon />,
      onClick: handleEditClick,
    },
    {
      name: 'delete',
      label: t('screens.cs.driver_manager.card.actions.delete'),
      icon: <DeleteIcon />,
      onClick: handleDeleteClick,
    },
  ], [])

  return (
    <>
      <KNDataTable
        columns={columns}
        actions={hasAccess(Role.Editor) ? actions : []}
        data={drivers}
        isLoading={isFetching > 0}
        sx={{
          marginX: -2,
        }}
      />
      {activeDriver && hasAccess(Role.Editor) && (
        <>
          <CreateOrEditDriverDialog
            payload={{
              driver: activeDriver,
            }}
            open={createOrEditDriverDialog.isOpen}
            onAction={createOrEditDriverDialog.handleAction}
            onClose={createOrEditDriverDialog.handleClose}
          />
          <DeleteDriverDialog
            payload={{
              driver: activeDriver,
            }}
            open={deleteDriverDialog.isOpen}
            onAction={deleteDriverDialog.handleAction}
            onClose={deleteDriverDialog.handleClose}
          />
        </>
      )}
    </>
  )
}

export default DriverTable
